import React, { useState, useEffect } from 'react';
import { Slider, Box } from '@mui/material';
import './MappingPanel.css';

const MappingPanel = ({ onSelectionChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hideNoDescription, setHideNoDescription] = useState(false);
  const [yearRange, setYearRange] = useState([1470, new Date().getFullYear()]);

  // Types de projets avec leurs descriptions
  const typeColors = {
    'Neuf': {
      color: '#4CAF50',
      description: 'Construction d\'un nouveau bâtiment'
    },
    'Rénovation': {
      color: '#2196F3',
      description: 'Modernisation d\'un bâtiment existant'
    },
    'Extension': {
      color: '#FF9800',
      description: 'Agrandissement d\'un bâtiment existant'
    },
    'Réhabilitation': {
      color: '#9C27B0',
      description: 'Restauration d\'un bâtiment à l\'état d\'origine'
    },
    'Réaffectation / Réutilisation': {
      color: '#795548',
      description: 'Changement de destination d\'un bâtiment'
    },
    'Restauration': {
      color: '#E91E63',
      description: 'Restauration d\'un bâtiment historique'
    },
    'Démolition / Reconstruction': {
      color: '#F44336',
      description: 'Démolition suivie d\'une nouvelle construction'
    }
  };

  // Programmes avec leurs descriptions et emojis
  const programColors = {
    'Agriculture et Environnement': {
      color: '#8BC34A',
      description: 'Fermes, serres, marchés agricoles, parcs naturels',
      emoji: '🌾'
    },
    'Commerce': {
      color: '#FF5722',
      description: 'Magasins, centres commerciaux',
      emoji: '🏪'
    },
    'Culturel': {
      color: '#9C27B0',
      description: 'Musées, galeries d\'art, théâtres, salles de concert, bibliothèques',
      emoji: '🎭'
    },
    'Educatif': {
      color: '#2196F3',
      description: 'Écoles, collèges, lycées, universités, centres de formation',
      emoji: '📚'
    },
    'Gouvernemental': {
      color: '#607D8B',
      description: 'Bâtiments administratifs, sièges de gouvernement, ambassades',
      emoji: '🏛️'
    },
    'Historique': {
      color: '#795548',
      description: 'Bâtiments patrimoniaux, monuments historiques, sites archéologiques',
      emoji: '🏺'
    },
    'Hôtellerie et Restauration': {
      color: '#FF9800',
      description: 'Hôtels, restaurants, bars, cafés',
      emoji: '🏨'
    },
    'Industriel': {
      color: '#455A64',
      description: 'Usines, entrepôts, complexes industriels',
      emoji: '🏭'
    },
    'Infrastructure': {
      color: '#607D8B',
      description: 'Stations d\'énergie, centres de traitement des eaux usées, ponts, tunnels',
      emoji: '⚡'
    },
    'Justice et Sécurité': {
      color: '#F44336',
      description: 'Tribunaux, commissariats, prisons, casernes de pompiers',
      emoji: '⚖️'
    },
    'Loisirs et Divertissements': {
      color: '#E91E63',
      description: 'Cinémas, centres de loisirs, parcs d\'attractions, salles de jeux',
      emoji: '🎮'
    },
    'Urbanisme et Espaces Publics': {
      color: '#4CAF50',
      description: 'Parcs publics, jardins, places, promenades, fontaines',
      emoji: '🌳'
    },
    'Résidentiel': {
      color: '#3F51B5',
      description: 'Maisons, appartements, résidences, logements sociaux',
      emoji: '🏠'
    },
    'Religieux': {
      color: '#9C27B0',
      description: 'Églises, mosquées, temples, synagogues, lieux de culte',
      emoji: '⛪'
    },
    'Santé': {
      color: '#E91E63',
      description: 'Hôpitaux, cliniques, centres de soins, maisons de retraite',
      emoji: '🏥'
    },
    'Science et Technologie': {
      color: '#00BCD4',
      description: 'Laboratoires, centres de recherche, observatoires',
      emoji: '🔬'
    },
    'Sportif': {
      color: '#FF5722',
      description: 'Stades, salles de sport, centres de fitness, piscines',
      emoji: '⚽'
    },
    'Services et Bureaux': {
      color: '#2196F3',
      description: 'Bureaux, services financiers, centres d\'appels, centres d\'affaires',
      emoji: '💼'
    },
    'Transport': {
      color: '#607D8B',
      description: 'Aéroports, gares, ports, stations de métro',
      emoji: '🚉'
    }
  };

  // Initialiser les états avec toutes les cases cochées
  const [selectedTypes, setSelectedTypes] = useState(
    Object.keys(typeColors).reduce((acc, type) => ({ ...acc, [type]: true }), {})
  );
  const [selectedPrograms, setSelectedPrograms] = useState(
    Object.keys(programColors).reduce((acc, program) => ({ ...acc, [program]: true }), {})
  );

  // Fonction pour convertir une année en position sur le slider (échelle non linéaire)
  const yearToPosition = (year) => {
    const minYear = 1470;
    const maxYear = new Date().getFullYear();
    const recentPeriod = 1900;
    
    if (year <= recentPeriod) {
      // Pour les années avant 1900, utiliser une échelle plus compressée
      return ((year - minYear) / (recentPeriod - minYear)) * 30;
    } else {
      // Pour les années après 1900, utiliser une échelle plus étirée
      return 30 + ((year - recentPeriod) / (maxYear - recentPeriod)) * 70;
    }
  };

  // Fonction pour convertir une position sur le slider en année
  const positionToYear = (position) => {
    const minYear = 1470;
    const maxYear = new Date().getFullYear();
    const recentPeriod = 1900;
    
    if (position <= 30) {
      // Pour les positions avant 30%, convertir en années avant 1900
      return Math.round(minYear + (position / 30) * (recentPeriod - minYear));
    } else {
      // Pour les positions après 30%, convertir en années après 1900
      return Math.round(recentPeriod + ((position - 30) / 70) * (maxYear - recentPeriod));
    }
  };

  // Créer les marqueurs pour le slider
  const marks = [
    { value: yearToPosition(1470), label: '1470' },
    { value: yearToPosition(1900), label: '1900' },
    { value: yearToPosition(new Date().getFullYear()), label: new Date().getFullYear().toString() }
  ];

  useEffect(() => {
    try {
      // Émettre les sélections actuelles
      const selectedTypesList = Object.entries(selectedTypes)
        .filter(([_, isSelected]) => isSelected)
        .map(([type]) => type);

      const selectedProgramsList = Object.entries(selectedPrograms)
        .filter(([_, isSelected]) => isSelected)
        .map(([program]) => program);

      onSelectionChange({
        types: selectedTypesList,
        programs: selectedProgramsList,
        hideNoDescription,
        yearRange
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour des sélections:', error);
    }
  }, [selectedTypes, selectedPrograms, hideNoDescription, yearRange, onSelectionChange]);

  const toggleType = (type) => {
    try {
      setSelectedTypes(prev => {
        const newState = {
          ...prev,
          [type]: !prev[type]
        };
        return newState;
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du type:', error);
      // Restaurer l'état précédent en cas d'erreur
      setSelectedTypes(prev => ({ ...prev }));
    }
  };

  const toggleProgram = (program) => {
    try {
      setSelectedPrograms(prev => {
        const newState = {
          ...prev,
          [program]: !prev[program]
        };
        return newState;
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du programme:', error);
      // Restaurer l'état précédent en cas d'erreur
      setSelectedPrograms(prev => ({ ...prev }));
    }
  };

  return (
    <div 
      className={`mapping-panel ${isOpen ? 'open' : ''}`}
      role="region"
      aria-label="Panel de mappage"
    >
      <button 
        className="mapping-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Fermer le panel de mappage' : 'Ouvrir le panel de mappage'}
        aria-expanded={isOpen}
        aria-controls="mapping-panel-content"
      >
        {isOpen ? '❮' : '❯'}
      </button>
      
      <div 
        id="mapping-panel-content"
        className="mapping-panel-content"
        role="region"
        aria-label="Contenu du panel de mappage"
        aria-hidden={!isOpen}
      >
        <h2>Mappage</h2>
        
        <div className="mapping-section" role="group" aria-label="Options générales">
          <div className="mapping-section-header">
            <h3>Options</h3>
          </div>
          <div className="mapping-items" role="listbox">
            <div 
              className="mapping-item" 
              onClick={() => setHideNoDescription(!hideNoDescription)}
              role="option"
              aria-selected={hideNoDescription}
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setHideNoDescription(!hideNoDescription);
                }
              }}
            >
              <div 
                className={`mapping-item-checkbox ${hideNoDescription ? 'checked' : ''}`}
                aria-hidden="true"
              />
              <span className="mapping-item-label">Masquer les projets sans description</span>
            </div>
          </div>
        </div>

        <div className="mapping-section" role="group" aria-label="Filtre par années">
          <div className="mapping-section-header">
            <h3>Filtre par années</h3>
          </div>
          <div className="mapping-items year-range" role="group">
            <Box sx={{ width: '90%', mx: 'auto' }}>
              <Slider
                value={[yearToPosition(yearRange[0]), yearToPosition(yearRange[1])]}
                onChange={(_, newValue) => {
                  setYearRange([
                    positionToYear(newValue[0]),
                    positionToYear(newValue[1])
                  ]);
                }}
                valueLabelDisplay="auto"
                marks={marks}
                min={0}
                max={100}
                valueLabelFormat={(value) => positionToYear(value)}
              />
            </Box>
          </div>
        </div>

        <div className="mapping-section" role="group" aria-label="Types de projets">
          <div className="mapping-section-header">
            <h3>Types de projets</h3>
          </div>
          <div className="mapping-items" role="listbox">
            {Object.entries(typeColors).map(([type, data]) => (
              <div 
                key={type} 
                className="mapping-item" 
                onClick={() => toggleType(type)}
                role="option"
                aria-selected={selectedTypes[type]}
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    toggleType(type);
                  }
                }}
              >
                <div 
                  className={`mapping-item-checkbox ${selectedTypes[type] ? 'checked' : ''}`}
                  aria-hidden="true"
                />
                <div 
                  className="mapping-item-color"
                  style={{ backgroundColor: data.color }}
                  aria-hidden="true"
                />
                <span className="mapping-item-label">{type}</span>
                <div className="mapping-item-tooltip" role="tooltip">{data.description}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mapping-section" role="group" aria-label="Programmes">
          <div className="mapping-section-header">
            <h3>Programmes</h3>
          </div>
          <div className="mapping-items" role="listbox">
            {Object.entries(programColors).map(([program, data]) => (
              <div 
                key={program} 
                className="mapping-item" 
                onClick={() => toggleProgram(program)}
                role="option"
                aria-selected={selectedPrograms[program]}
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    toggleProgram(program);
                  }
                }}
              >
                <div 
                  className={`mapping-item-checkbox ${selectedPrograms[program] ? 'checked' : ''}`}
                  aria-hidden="true"
                />
                <span className="mapping-item-label">
                  <span className="mapping-item-emoji" aria-hidden="true">{data.emoji}</span>
                  {program}
                </span>
                <div className="mapping-item-tooltip" role="tooltip">{data.description}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MappingPanel;